/**
 * Bundable: per-line minimum quantity before Add to Cart.
 * Runs only after the widget finishes loading (no .bdlr__loading).
 */
(() => {
  const DEFAULT_MIN = Math.max(1, Number(window.EarthHeroBundleConfig?.defaultMinQty) || 12);

  const ROOT_SELECTORS = [
    '.bundable-widget-container',
    '.bdlr__bundle-widget--container',
    '.bdlr__bundle-widget--container.row',
  ];

  const READY_MARKERS =
    '.bdlr__products-grid, .bdlr__products--container, .bdlr__layout, .bdlr__product-card, .enoble_bundle-item';

  const QTY_SELECTORS = [
    '.bdlr__summary--list-item .bdlr__qty-input',
    '.bdlr__summary--list-item input[type="number"]',
    '.bdlr__qty-input',
    '.bdlr__summary-qty-input',
    '.bdlr_summary-qty-input',
  ].join(',');

  const ITEM_SELECTORS = [
    '.bdlr__summary--list-item',
    'li.bdlr__summary--list-item',
    '.bdlr__summary-item',
    '.bdlr_summary-item',
  ];

  const CHECKOUT_SELECTORS = [
    '#bdlr__summary-checkout--create-bundle',
    '.bdlr__checkout-button',
    '.bdlr__btn--checkout',
    '.bdlr_btn--checkout',
  ].join(',');

  const DISCLAIMER_SELECTORS = '.bdlr__summary-disclaimer, .bdlr_summary-disclaimer, .bdlr__summary-message';

  const TITLE_SELECTORS = [
    '.name.bdlr__summary--list-item-cell',
    '.bdlr__summary-item-title',
    '.bdlr_summary-item-title',
  ];

  let started = false;
  let applying = false;
  let rootObserver = null;
  let debounceTimer = null;

  const getRoot = () => {
    for (const sel of ROOT_SELECTORS) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    return null;
  };

  const isWidgetReady = (root) => {
    if (!root) return false;
    if (root.querySelector('.bdlr__loading')) return false;
    return !!root.querySelector(READY_MARKERS);
  };

  const scheduleApply = () => {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => applyValidation(), 150);
  };

  const closestItem = (input) => {
    for (const sel of ITEM_SELECTORS) {
      const el = input.closest(sel);
      if (el) return el;
    }
    return input.closest('[class*="summary"]');
  };

  const getQtyInputs = (root) =>
    [...root.querySelectorAll(QTY_SELECTORS)].filter((el) => el.tagName === 'INPUT');

  /** Native min>1 makes number spinners jump (e.g. 1 → 12). Keep stepping at 1. */
  const ensureQtyInputStepping = (input) => {
    if (!input || input.tagName !== 'INPUT') return;
    if (input.getAttribute('min') !== '1') input.setAttribute('min', '1');
    if (!input.getAttribute('step')) input.setAttribute('step', '1');
    input.setCustomValidity('');
  };

  const getSummaryRows = (root) =>
    getQtyInputs(root).map((input) => {
      const item = closestItem(input);
      let title = 'This product';
      for (const sel of TITLE_SELECTORS) {
        const titleEl = item?.querySelector(sel);
        if (titleEl?.textContent?.trim()) {
          title = titleEl.textContent.trim();
          break;
        }
      }
      const qty = parseInt(input.value, 10) || 0;
      const min = DEFAULT_MIN;
      return { input, item, title, qty, min, valid: qty >= min };
    });

  const findCheckout = (root) => {
    for (const sel of CHECKOUT_SELECTORS.split(',')) {
      const btn = root.querySelector(sel.trim());
      if (btn) return btn;
    }
    return [...root.querySelectorAll('button')].find((btn) => {
      const text = (btn.textContent || '').toLowerCase();
      return text.includes('add to cart') || text.includes('create bundle');
    });
  };

  const findDisclaimer = (root) => root.querySelector(DISCLAIMER_SELECTORS);

  const buildMessage = (rows) => {
    if (!rows.length) return 'Add products to your bundle to get started.';
    const invalid = rows.filter((r) => !r.valid);
    if (!invalid.length) return 'Your bundle meets all minimum quantities. You can add to cart.';
    if (invalid.length === 1) {
      const { title, min, qty } = invalid[0];
      return `${title}: minimum quantity is ${min} (currently ${qty}). Add ${min - qty} more to continue.`;
    }
    return invalid.map(({ title, min, qty }) => `${title} needs at least ${min} (has ${qty})`).join(' · ');
  };

  const applyValidation = () => {
    if (applying) return false;
    const root = getRoot();
    if (!isWidgetReady(root)) return false;

    applying = true;
    try {
      const rows = getSummaryRows(root);
      const allValid = rows.length > 0 && rows.every((r) => r.valid);

      rows.forEach(({ input, item, min, valid }) => {
        input.setAttribute('data-bundle-min-qty', String(min));
        ensureQtyInputStepping(input);
        if (item) item.classList.toggle('bdlr__summary-item--below-min', !valid);
      });

      const checkout = findCheckout(root);
      if (checkout && checkout.disabled !== !allValid) {
        checkout.disabled = !allValid;
      }

      const disclaimer = findDisclaimer(root);
      if (disclaimer) {
        const msg = buildMessage(rows);
        if (disclaimer.textContent !== msg) disclaimer.textContent = msg;
      }

      return true;
    } finally {
      applying = false;
    }
  };

  const onQtyPointer = (event) => {
    const root = getRoot();
    if (!root || !isWidgetReady(root)) return;
    const input = event.target.matches?.(QTY_SELECTORS)
      ? event.target
      : event.target.closest?.(QTY_SELECTORS);
    if (!input || !root.contains(input)) return;
    ensureQtyInputStepping(input);
  };

  const onInteraction = (event) => {
    const root = getRoot();
    if (!root || !isWidgetReady(root) || !root.contains(event.target)) return;

    if (event.target.matches?.(QTY_SELECTORS)) {
      ensureQtyInputStepping(event.target);
      scheduleApply();
    }

    const checkout = event.target.closest?.(CHECKOUT_SELECTORS);
    if (checkout && root.contains(checkout)) {
      const rows = getSummaryRows(root);
      if (!rows.length || !rows.every((r) => r.valid)) {
        event.preventDefault();
        event.stopPropagation();
        applyValidation();
      }
    }
  };

  const start = () => {
    const root = getRoot();
    if (!isWidgetReady(root)) return;

    if (!started) {
      started = true;
      document.addEventListener('mousedown', onQtyPointer, true);
      document.addEventListener('keydown', onQtyPointer, true);
      document.addEventListener('input', onInteraction, true);
      document.addEventListener('change', onInteraction, true);
      document.addEventListener('click', onInteraction, true);

      if (!rootObserver) {
        rootObserver = new MutationObserver(scheduleApply);
        rootObserver.observe(root, { childList: true, subtree: true });
      }
    }

    applyValidation();
  };

  const waitForWidget = () => {
    const root = getRoot();
    if (isWidgetReady(root)) {
      start();
      return;
    }

    const observer = new MutationObserver(() => {
      if (isWidgetReady(getRoot())) {
        observer.disconnect();
        start();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 300000);
  };

  const boot = () => waitForWidget();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('load', boot);
  document.addEventListener('shopify:section:load', boot);
})();
